import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface AssignedDrinkBarDto {
    eventBarId: number;
    barName: string;
    sortOrder?: number;
    assigned: number;
    used: number;
    remaining: number;
}

interface AssignedDrinkPersonDto {
    personId: number;
    fullName: string;
    functionName?: string;
    bars: AssignedDrinkBarDto[];
    totalAssigned: number;
    totalUsed: number;
    totalRemaining: number;
    validDate?: string | null;
}

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;
    if (Array.isArray(result?.events)) return result.events;

    return [];
};

const toNumber = (value: any, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const normalizeEvent = (x: any): EventDto => {
    return {
        eventID: toNumber(
            x?.eventID ??
            x?.EventID ??
            x?.eventId ??
            x?.EventId ??
            x?.id ??
            x?.Id
        ),

        eventName:
            x?.eventName ??
            x?.EventName ??
            x?.name ??
            x?.Name ??
            ""
    };
};

const getTodayString = () => {
    const today = new Date();

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-GB");
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const AdminDrinkAssignedPersons: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [rows, setRows] = useState<AssignedDrinkPersonDto[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedName, setSelectedName] =
        useState<string | null>(null);

    const [selectedFunction, setSelectedFunction] =
        useState<string | null>(null);

    const [validDate, setValidDate] =
        useState<string>(getTodayString());

    // ---------------------------------------------------------
    // LOAD EVENTS
    // ---------------------------------------------------------

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/events/light`
                );

                if (!res.ok) {
                    console.error(
                        "Could not load events:",
                        res.status
                    );

                    return;
                }

                const data = await res.json();

                const mapped = toArray<any>(data)
                    .map(normalizeEvent)
                    .filter((e) => e.eventID > 0);

                setEvents(mapped);
            } catch (err) {
                console.error(
                    "Error loading events:",
                    err
                );
            }
        };

        loadEvents();
    }, [apiUrl]);

    // ---------------------------------------------------------
    // LOAD ASSIGNED PERSONS
    // ---------------------------------------------------------

    const fetchAssignedPersons = async (
        currentEventId: number,
        currentValidDate: string
    ) => {
        if (!currentEventId || !currentValidDate) {
            setRows([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const res = await fetch(
                `${apiUrl}/api/drinks/assigned-persons` +
                `?eventId=${currentEventId}` +
                `&validDate=${encodeURIComponent(currentValidDate)}`
            );

            if (!res.ok) {
                const text = await res.text();

                throw new Error(
                    text ||
                    "Could not fetch assigned drinks."
                );
            }

            const data = await res.json();

            const mapped =
                toArray<any>(data).map(
                    (x): AssignedDrinkPersonDto => ({
                        personId: toNumber(
                            x.personId ??
                            x.PersonId
                        ),

                        fullName:
                            x.fullName ??
                            x.FullName ??
                            `${x.firstName ?? x.FirstName ?? ""} ${x.lastName ?? x.LastName ?? ""
                                }`.trim(),

                        functionName:
                            x.functionName ??
                            x.FunctionName ??
                            "",

                        bars: toArray<any>(
                            x.bars ??
                            x.Bars
                        ).map(
                            (
                                bar
                            ): AssignedDrinkBarDto => ({
                                eventBarId: toNumber(
                                    bar.eventBarId ??
                                    bar.EventBarId
                                ),

                                barName:
                                    bar.barName ??
                                    bar.BarName ??
                                    "",

                                sortOrder: toNumber(
                                    bar.sortOrder ??
                                    bar.SortOrder
                                ),

                                assigned: toNumber(
                                    bar.assigned ??
                                    bar.Assigned
                                ),

                                used: toNumber(
                                    bar.used ??
                                    bar.Used
                                ),

                                remaining: toNumber(
                                    bar.remaining ??
                                    bar.Remaining
                                )
                            })
                        ),

                        totalAssigned: toNumber(
                            x.totalAssigned ??
                            x.TotalAssigned
                        ),

                        totalUsed: toNumber(
                            x.totalUsed ??
                            x.TotalUsed
                        ),

                        totalRemaining: toNumber(
                            x.totalRemaining ??
                            x.TotalRemaining
                        ),

                        validDate:
                            x.validDate ??
                            x.ValidDate ??
                            null
                    })
                );

            setRows(mapped);
        } catch (err: any) {
            console.error(
                "Error loading assigned drinks:",
                err
            );

            setRows([]);

            setError(
                err?.message ||
                "Could not fetch assigned drinks."
            );
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------
    // RELOAD WHEN EVENT / DATE CHANGES
    // ---------------------------------------------------------

    useEffect(() => {
        setSelectedName(null);
        setSelectedFunction(null);

        if (eventId > 0 && validDate) {
            fetchAssignedPersons(
                eventId,
                validDate
            );
        } else {
            setRows([]);
        }
    }, [eventId, validDate]);

    // ---------------------------------------------------------
    // AVAILABLE BARS
    // ---------------------------------------------------------

    const availableBars = useMemo(() => {
        const map = new Map<
            number,
            {
                eventBarId: number;
                barName: string;
                sortOrder: number;
            }
        >();

        rows.forEach((row) => {
            row.bars.forEach((bar) => {
                if (!map.has(bar.eventBarId)) {
                    map.set(
                        bar.eventBarId,
                        {
                            eventBarId:
                                bar.eventBarId,

                            barName:
                                bar.barName ||
                                `Bar ${bar.eventBarId}`,

                            sortOrder:
                                bar.sortOrder ?? 0
                        }
                    );
                }
            });
        });

        return Array.from(map.values())
            .sort((a, b) => {
                if (
                    a.sortOrder !==
                    b.sortOrder
                ) {
                    return (
                        a.sortOrder -
                        b.sortOrder
                    );
                }

                return a.barName.localeCompare(
                    b.barName,
                    "da"
                );
            });
    }, [rows]);

    // ---------------------------------------------------------
    // FILTER OPTIONS
    // ---------------------------------------------------------

    const availableNames = useMemo(() => {
        return Array.from(
            new Set(
                rows
                    .map(
                        (x) =>
                            x.fullName || ""
                    )
                    .filter(
                        (x) =>
                            !!x.trim()
                    )
            )
        ).sort((a, b) =>
            a.localeCompare(b, "da")
        );
    }, [rows]);

    const availableFunctions = useMemo(() => {
        return Array.from(
            new Set(
                rows
                    .map(
                        (x) =>
                            x.functionName ||
                            ""
                    )
                    .filter(
                        (x) =>
                            !!x.trim()
                    )
            )
        ).sort((a, b) =>
            a.localeCompare(b, "da")
        );
    }, [rows]);

    // ---------------------------------------------------------
    // FILTER ROWS
    // ---------------------------------------------------------

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const matchesName =
                !selectedName ||
                row.fullName ===
                selectedName;

            const matchesFunction =
                !selectedFunction ||
                (row.functionName ||
                    "") ===
                selectedFunction;

            return (
                matchesName &&
                matchesFunction
            );
        });
    }, [
        rows,
        selectedName,
        selectedFunction
    ]);

    // ---------------------------------------------------------
    // TOTALS
    // ---------------------------------------------------------

    const totals = useMemo(() => {
        const barTotals = new Map<
            number,
            {
                assigned: number;
                used: number;
                remaining: number;
            }
        >();

        let totalAssigned = 0;
        let totalUsed = 0;
        let totalRemaining = 0;

        filteredRows.forEach((row) => {
            totalAssigned +=
                row.totalAssigned;

            totalUsed +=
                row.totalUsed;

            totalRemaining +=
                row.totalRemaining;

            row.bars.forEach((bar) => {
                const current =
                    barTotals.get(
                        bar.eventBarId
                    ) ?? {
                        assigned: 0,
                        used: 0,
                        remaining: 0
                    };

                current.assigned +=
                    bar.assigned;

                current.used +=
                    bar.used;

                current.remaining +=
                    bar.remaining;

                barTotals.set(
                    bar.eventBarId,
                    current
                );
            });
        });

        return {
            barTotals,
            totalAssigned,
            totalUsed,
            totalRemaining
        };
    }, [filteredRows]);

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div className="dashContainer">
            <div className="wrapper">

                <div className="top">
                    <div className="name">
                        Assigned Drinks Overview
                    </div>

                    <div className="meta">
                        See all persons with assigned
                        drinks, personal and by function
                    </div>
                </div>

                <div className="content">

                    {error && (
                        <div
                            className="badgeRed"
                            style={{
                                padding: 8,
                                marginBottom: 16
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <div className="sectionTitle">
                        Filters
                    </div>

                    <div
                        className="statsGrid3"
                        style={{
                            marginTop: 12
                        }}
                    >

                        {/* EVENT */}

                        <div
                            className="stat"
                            style={{
                                padding: 16
                            }}
                        >
                            <label>
                                Event
                            </label>

                            <Select
                                placeholder="Select event"
                                value={
                                    eventId
                                        ? String(
                                            eventId
                                        )
                                        : null
                                }
                                onChange={(
                                    val
                                ) =>
                                    setEventId(
                                        val
                                            ? Number(
                                                val
                                            )
                                            : 0
                                    )
                                }
                                data={events.map(
                                    (e) => ({
                                        value:
                                            String(
                                                e.eventID
                                            ),

                                        label:
                                            e.eventName ||
                                            `Event ${e.eventID}`
                                    })
                                )}
                                nothingFoundMessage="No events found"
                            />
                        </div>

                        {/* NAME */}

                        <div
                            className="stat"
                            style={{
                                padding: 16
                            }}
                        >
                            <label>
                                Select name
                            </label>

                            <Select
                                searchable
                                clearable
                                placeholder="Select name"
                                value={
                                    selectedName
                                }
                                onChange={
                                    setSelectedName
                                }
                                data={availableNames.map(
                                    (
                                        name
                                    ) => ({
                                        value:
                                            name,

                                        label:
                                            name
                                    })
                                )}
                                nothingFoundMessage="No names found"
                            />
                        </div>

                        {/* FUNCTION */}

                        <div
                            className="stat"
                            style={{
                                padding: 16
                            }}
                        >
                            <label>
                                Filter by function
                            </label>

                            <Select
                                searchable
                                clearable
                                placeholder="Select function"
                                value={
                                    selectedFunction
                                }
                                onChange={
                                    setSelectedFunction
                                }
                                data={availableFunctions.map(
                                    (
                                        fn
                                    ) => ({
                                        value:
                                            fn,

                                        label:
                                            fn
                                    })
                                )}
                                nothingFoundMessage="No functions found"
                            />
                        </div>
                    </div>

                    <div
                        className="statsGrid3"
                        style={{
                            marginTop: 12
                        }}
                    >

                        {/* DATE */}

                        <div
                            className="stat"
                            style={{
                                padding: 16
                            }}
                        >
                            <label>
                                Valid date
                            </label>

                            <input
                                type="date"
                                value={
                                    validDate
                                }
                                onChange={(
                                    e
                                ) =>
                                    setValidDate(
                                        e.target
                                            .value
                                    )
                                }
                            />
                        </div>

                        <div
                            className="stat"
                            style={{
                                padding: 16,
                                display:
                                    "flex",
                                alignItems:
                                    "end"
                            }}
                        >
                            <div>
                                Selected date:{" "}
                                <strong>
                                    {formatDate(
                                        validDate
                                    )}
                                </strong>
                            </div>
                        </div>
                    </div>

                    {/* LOADING */}

                    {loading && (
                        <div
                            style={{
                                marginTop: 20
                            }}
                        >
                            Loading assigned
                            drinks...
                        </div>
                    )}

                    {/* RESULTS */}

                    {!loading &&
                        filteredRows.length >
                        0 && (
                            <>
                                <div
                                    className="sectionTitle"
                                    style={{
                                        marginTop: 30
                                    }}
                                >
                                    Result
                                </div>

                                <div
                                    className="stat"
                                    style={{
                                        padding: 0,
                                        marginTop: 12,
                                        overflowX:
                                            "auto"
                                    }}
                                >
                                    <table
                                        style={{
                                            width:
                                                "100%",

                                            borderCollapse:
                                                "collapse",

                                            minWidth:
                                                Math.max(
                                                    900,
                                                    500 +
                                                    availableBars.length *
                                                    160
                                                )
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th
                                                    style={
                                                        thLeft
                                                    }
                                                >
                                                    Name
                                                </th>

                                                <th
                                                    style={
                                                        thLeft
                                                    }
                                                >
                                                    Function
                                                </th>

                                                {availableBars.map(
                                                    (
                                                        bar
                                                    ) => (
                                                        <th
                                                            key={
                                                                bar.eventBarId
                                                            }
                                                            style={
                                                                th
                                                            }
                                                        >
                                                            {
                                                                bar.barName
                                                            }
                                                        </th>
                                                    )
                                                )}

                                                <th
                                                    style={
                                                        th
                                                    }
                                                >
                                                    Used
                                                </th>

                                                <th
                                                    style={
                                                        th
                                                    }
                                                >
                                                    Assigned
                                                </th>

                                                <th
                                                    style={
                                                        th
                                                    }
                                                >
                                                    Remaining
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredRows.map(
                                                (
                                                    row
                                                ) => (
                                                    <tr
                                                        key={
                                                            row.personId
                                                        }
                                                    >
                                                        <td
                                                            style={
                                                                tdLeft
                                                            }
                                                        >
                                                            {
                                                                row.fullName
                                                            }
                                                        </td>

                                                        <td
                                                            style={
                                                                tdLeft
                                                            }
                                                        >
                                                            {row.functionName ||
                                                                "-"}
                                                        </td>

                                                        {availableBars.map(
                                                            (
                                                                bar
                                                            ) => {
                                                                const personBar =
                                                                    row.bars.find(
                                                                        (
                                                                            x
                                                                        ) =>
                                                                            x.eventBarId ===
                                                                            bar.eventBarId
                                                                    );

                                                                return (
                                                                    <td
                                                                        key={
                                                                            bar.eventBarId
                                                                        }
                                                                        style={
                                                                            td
                                                                        }
                                                                    >
                                                                        {personBar?.assigned ??
                                                                            0}

                                                                        {(personBar?.used ??
                                                                            0) >
                                                                            0 && (
                                                                                <div
                                                                                    style={
                                                                                        usedTextStyle
                                                                                    }
                                                                                >
                                                                                    used:{" "}
                                                                                    {personBar?.used ??
                                                                                        0}
                                                                                </div>
                                                                            )}

                                                                        {(personBar?.remaining ??
                                                                            0) >
                                                                            0 && (
                                                                                <div
                                                                                    style={
                                                                                        remainingTextStyle
                                                                                    }
                                                                                >
                                                                                    remaining:{" "}
                                                                                    {personBar?.remaining ??
                                                                                        0}
                                                                                </div>
                                                                            )}
                                                                    </td>
                                                                );
                                                            }
                                                        )}

                                                        <td
                                                            style={
                                                                td
                                                            }
                                                        >
                                                            {
                                                                row.totalUsed
                                                            }
                                                        </td>

                                                        <td
                                                            style={{
                                                                ...td,
                                                                fontWeight: 800
                                                            }}
                                                        >
                                                            {
                                                                row.totalAssigned
                                                            }
                                                        </td>

                                                        <td
                                                            style={{
                                                                ...td,
                                                                fontWeight: 800
                                                            }}
                                                        >
                                                            {
                                                                row.totalRemaining
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>

                                        <tfoot>
                                            <tr
                                                style={{
                                                    fontWeight: 800
                                                }}
                                            >
                                                <td
                                                    style={
                                                        tdLeft
                                                    }
                                                >
                                                    TOTAL
                                                </td>

                                                <td
                                                    style={
                                                        tdLeft
                                                    }
                                                ></td>

                                                {availableBars.map(
                                                    (
                                                        bar
                                                    ) => {
                                                        const barTotal =
                                                            totals.barTotals.get(
                                                                bar.eventBarId
                                                            );

                                                        return (
                                                            <td
                                                                key={
                                                                    bar.eventBarId
                                                                }
                                                                style={
                                                                    td
                                                                }
                                                            >
                                                                {barTotal?.assigned ??
                                                                    0}

                                                                {(barTotal?.used ??
                                                                    0) >
                                                                    0 && (
                                                                        <div
                                                                            style={
                                                                                usedTextStyle
                                                                            }
                                                                        >
                                                                            used:{" "}
                                                                            {barTotal?.used ??
                                                                                0}
                                                                        </div>
                                                                    )}

                                                                {(barTotal?.remaining ??
                                                                    0) >
                                                                    0 && (
                                                                        <div
                                                                            style={
                                                                                remainingTextStyle
                                                                            }
                                                                        >
                                                                            remaining:{" "}
                                                                            {barTotal?.remaining ??
                                                                                0}
                                                                        </div>
                                                                    )}
                                                            </td>
                                                        );
                                                    }
                                                )}

                                                <td
                                                    style={
                                                        td
                                                    }
                                                >
                                                    {
                                                        totals.totalUsed
                                                    }
                                                </td>

                                                <td
                                                    style={
                                                        td
                                                    }
                                                >
                                                    {
                                                        totals.totalAssigned
                                                    }
                                                </td>

                                                <td
                                                    style={
                                                        td
                                                    }
                                                >
                                                    {
                                                        totals.totalRemaining
                                                    }
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </>
                        )}

                    {!loading &&
                        eventId > 0 &&
                        filteredRows.length ===
                        0 && (
                            <div
                                style={{
                                    marginTop: 20
                                }}
                            >
                                No assigned drinks
                                found.
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------
// TABLE STYLES
// ---------------------------------------------------------

const th = {
    padding: "12px",
    borderBottom:
        "1px solid rgba(0,0,0,0.08)",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const
};

const thLeft = {
    ...th,
    textAlign: "left" as const
};

const td = {
    padding: "10px",
    borderBottom:
        "1px solid rgba(0,0,0,0.06)",
    textAlign: "center" as const,
    verticalAlign: "top" as const
};

const tdLeft = {
    ...td,
    textAlign: "left" as const
};

const usedTextStyle = {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.7
};

const remainingTextStyle = {
    marginTop: 2,
    fontSize: 12,
    opacity: 0.7
};

export default AdminDrinkAssignedPersons;